import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { OrderService } from '../../../features/live-orders/order.service';
import { RefundService } from '../../../core/services/refund.service';

export type RefundMode = 'full' | 'items' | 'partial';

export interface RefundPayload {
  orderId: string;
  amount: number;
  reason?: string;
  mode: RefundMode;
  itemLines?: Array<{ key: string; qty: number }>;
}

export interface RefundResult {
  orderId: string;
  refundId: string;
  amount: number;
  currency: string;
  createdAt: string;
  status: 'pending' | 'succeeded' | 'failed';
  message?: string;
}

type ItemFG = FormGroup<{
  key: FormControl<string>;
  name: FormControl<string>;
  purchQty: FormControl<number>;
  unitPrice: FormControl<number>;
  refundQty: FormControl<number>;
  backendId: FormControl<number | null>;
}>;

@Component({
  selector: 'app-refund-dialog',
  standalone: false,
  templateUrl: './refund-dialog.component.html',
  styleUrl: './refund-dialog.component.scss',
})
export class RefundDialogComponent implements OnInit, OnDestroy {
  @Input() orderId!: string;
  @Input() publicOrderCode!: string;
  @Output() completed = new EventEmitter<RefundResult>();
  @Output() cancelled = new EventEmitter<void>();

  loading = true;
  submitting = false;
  error: string | null = null;

  details: any = null;
  refundableTotal = 0;

  form!: FormGroup;

  private sub?: Subscription;
  private formSubs = new Subscription();
  private destroyed = false;

  constructor(
    private fb: FormBuilder,
    private orderService: OrderService,
    private refundService: RefundService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sub?.unsubscribe();
    this.formSubs.unsubscribe();
  }

  private updateViewState(update: () => void): void {
    this.zone.run(() => {
      update();
      this.scheduleViewRefresh();
    });
  }

  private scheduleViewRefresh(): void {
    Promise.resolve().then(() => {
      if (!this.destroyed) {
        this.cdr.detectChanges();
      }
    });
  }

  /** Build the form once details are available */
  private buildForm() {
    const total = this.refundableTotal;
    this.formSubs.unsubscribe();
    this.formSubs = new Subscription();

    this.form = this.fb.group({
      mode: ['full' as RefundMode, Validators.required], // 'full' | 'items' | 'partial'
      amount: [{ value: total, disabled: true }, [Validators.min(0.01)]],
      reason: [''],
      items: this.fb.array(this.toItemControls()),
    });

    // Toggle amount enable/disable based on mode
    this.formSubs.add(
      this.form.get('mode')!.valueChanges.subscribe((m: RefundMode) => {
        const amountCtrl = this.form.get('amount')!;
        if (m === 'full') {
          amountCtrl.disable();
          amountCtrl.setValue(total, { emitEvent: false });
        } else if (m === 'items') {
          amountCtrl.disable();
          this.autoSumItemsIntoAmount();
        } else {
          // partial
          amountCtrl.enable();
          if (amountCtrl.value == null || amountCtrl.disabled) {
            amountCtrl.setValue(0.0, { emitEvent: false });
          }
        }
      }),
    );

    // Auto-sum when items toggled (only in partial)
    this.items.controls.forEach((ctrl) => {
      this.formSubs.add(
        ctrl.get('refundQty')!.valueChanges.subscribe(() => {
          if (this.form.get('mode')!.value === 'items') {
            this.autoSumItemsIntoAmount();
          }
        }),
      );
    });
  }

  /** Convert response list into form group controls */
  private toItemControls(): ItemFG[] {
    const list = Array.isArray(this.details?.details)
      ? this.details.details
      : [];

    return list.map((it: any, idx: number) => {
      const purchQty = this.coerceNum(it?.itemQuantity, 1);
      const lineTotal = this.coerceNum(
        it?.itemTotalGrossOriginal ?? it?.itemPrice,
        0,
      );
      const perUnit = purchQty > 0 ? lineTotal / purchQty : lineTotal;

      // Try common id fields from your payload
      const backendId = this.pickItemId(it, idx);

      return this.fb.group({
        key: this.fb.control(this.itemKey(it, idx)),
        name: this.fb.control(it?.itemName || `Item ${idx + 1}`),
        purchQty: this.fb.control(purchQty, { nonNullable: true }),
        unitPrice: this.fb.control(Number(perUnit.toFixed(2)), {
          nonNullable: true,
        }),
        refundQty: this.fb.control(0, { nonNullable: true }),
        backendId: this.fb.control(backendId), // may be null if not present
      }) as ItemFG;
    });
  }

  /** Create a stable-ish key for backend mapping (if you decide to use it) */
  private itemKey(it: any, idx: number) {
    const name = (it?.itemName || '').toString().trim();
    return name ? `${idx}:${name}` : `idx:${idx}`;
  }

  /** Load order details and prep form */
  private load(force = true) {
    this.updateViewState(() => {
      this.loading = true;
      this.error = null;
      this.details = null;
    });

    this.sub = this.orderService
      .viewOrderDetails(this.orderId, force)
      .subscribe({
        next: (d) => {
          this.updateViewState(() => {
            this.details = d;

            const explicitRefundable = this.coerceNum(
              d?.refundableAmount ??
                d?.capturedAmount ??
                d?.orderTotalCurrentGross ??
                d?.total,
              0,
            );
            const alreadyRefunded = this.coerceNum(d?.amountRefunded, 0);
            this.refundableTotal = Math.max(
              0,
              explicitRefundable - alreadyRefunded,
            );

            this.buildForm();
            this.loading = false;
          });
        },
        error: () => {
          this.updateViewState(() => {
            this.error = 'Failed to load order.';
            this.loading = false;
          });
        },
      });
  }

  private pickItemId(it: any, idx: number): number | null {
    const candidates = [it?.foodOrderItemId];

    for (const c of candidates) {
      const n =
        typeof c === 'string' ? Number(c) : typeof c === 'number' ? c : NaN;
      if (!isNaN(n) && n > 0) return n;
    }
    return null; // guarded in submit()
  }

  /** Coerce numeric fields safely */
  private coerceNum(x: any, fallback = 0): number {
    const n =
      typeof x === 'string' ? Number(x) : typeof x === 'number' ? x : NaN;
    return isNaN(n) ? fallback : n;
  }

  /** Auto-sum partial refund from selected items */
  private autoSumItemsIntoAmount() {
    const sum = this.items.controls.reduce((acc, c) => {
      const qty = this.coerceNum(c.get('refundQty')!.value, 0);
      const price = this.coerceNum(c.get('unitPrice')!.value, 0);
      return acc + qty * price;
    }, 0);
    const clamped = Math.min(sum, this.refundableTotal);
    this.form
      .get('amount')!
      .setValue(Number(clamped.toFixed(2)), { emitEvent: false });
  }

  /** Submit refund */
  submit() {
    if (!this.form || !this.form.valid) return;

    this.updateViewState(() => {
      this.submitting = true;
      this.error = null;
    });

    const mode = this.form.get('mode')!.value as RefundMode;

    if (mode === 'full') {
      const comment = (this.form.get('reason')!.value || '').toString().trim();

      this.refundService.fullRefund(this.orderId, comment).subscribe({
        next: (res) => {
          if (res.status === 'succeeded') {
            this.updateViewState(() => {
              this.submitting = false;
            });
            this.completed.emit(res); // parent will refresh & close
          } else if (res.status === 'pending') {
            this.updateViewState(() => {
              this.submitting = false;
              this.error =
                res.message || 'Refund is pending. Please try again later.';
            });
          } else {
            this.updateViewState(() => {
              this.submitting = false;
              this.error = res.message || 'Refund failed.';
            });
          }
        },
        error: (err) => {
          this.updateViewState(() => {
            this.submitting = false;
            this.error =
              err?.error?.message || err?.message || 'Refund failed.';
          });
        },
      });
      return;
    }

    // items / partial not wired yet
    if (mode === 'items') {
      // Build lines (id + qty) and compute amount for preview/fallback
      const lines = this.items.controls
        .map((c) => {
          const id = c.get('backendId')!.value;
          const qty = this.coerceNum(c.get('refundQty')!.value, 0);
          const unit = this.coerceNum(c.get('unitPrice')!.value, 0);
          return {
            id: typeof id === 'number' ? id : NaN,
            qty,
            lineTotal: qty * unit,
          };
        })
        .filter((l) => l.qty > 0);

      if (!lines.length) {
        this.updateViewState(() => {
          this.submitting = false;
          this.error = 'Select at least one item to refund.';
        });
        return;
      }

      // Ensure all selected lines have a valid backend id
      const invalid = lines.find((l) => !isFinite(l.id) || l.id <= 0);
      if (invalid) {
        this.updateViewState(() => {
          this.submitting = false;
          this.error =
            'One or more selected items have no backend id. Please refresh or contact support.';
        });
        return;
      }

      let amount = lines.reduce((s, l) => s + l.lineTotal, 0);
      if (amount <= 0) {
        this.updateViewState(() => {
          this.submitting = false;
          this.error = 'Calculated amount is 0. Please adjust quantities.';
        });
        return;
      }
      if (amount > this.refundableTotal) {
        this.updateViewState(() => {
          this.submitting = false;
          this.error = `Amount cannot exceed ${this.refundableTotal.toFixed(2)}.`;
        });
        return;
      }
      amount = Number(amount.toFixed(2));

      const comment = (this.form.get('reason')!.value || '').toString().trim();

      this.refundService
        .itemRefund(
          this.orderId,
          lines.map(({ id, qty }) => ({ id, qty })),
          comment,
          amount, // used as fallback in normalize if API doesn't echo amount
        )
        .subscribe({
          next: (res) => {
            if (res.status === 'succeeded') {
              this.updateViewState(() => {
                this.submitting = false;
              });
              this.completed.emit(res); // parent will refresh, close modals, toast
            } else if (res.status === 'pending') {
              this.updateViewState(() => {
                this.submitting = false;
                this.error =
                  res.message || 'Refund is pending. Please try again later.';
              });
            } else {
              this.updateViewState(() => {
                this.submitting = false;
                this.error = res.message || 'Refund failed.';
              });
            }
          },
          error: (err) => {
            this.updateViewState(() => {
              this.submitting = false;
              this.error =
                err?.error?.message || err?.message || 'Refund failed.';
            });
          },
        });

      return;
    }

    if (mode === 'partial') {
      const raw = this.form.get('amount')!.value;
      const amount = Number(
        (typeof raw === 'string' ? parseFloat(raw) : (raw ?? 0)).toFixed(2),
      );

      if (amount <= 0) {
        this.updateViewState(() => {
          this.submitting = false;
          this.error = 'Amount must be greater than 0.';
        });
        return;
      }
      if (amount > this.refundableTotal) {
        this.updateViewState(() => {
          this.submitting = false;
          this.error = `Amount cannot exceed ${this.refundableTotal.toFixed(2)}.`;
        });
        return;
      }

      const comment = (this.form.get('reason')!.value || '').toString().trim();

      this.refundService
        .partialRefund(this.orderId, amount, comment)
        .subscribe({
          next: (res) => {
            if (res.status === 'succeeded') {
              this.updateViewState(() => {
                this.submitting = false;
              });
              this.completed.emit(res); // parent will refresh, close modals, and toast
            } else if (res.status === 'pending') {
              this.updateViewState(() => {
                this.submitting = false;
                this.error =
                  res.message || 'Refund is pending. Please try again later.';
              });
            } else {
              this.updateViewState(() => {
                this.submitting = false;
                this.error = res.message || 'Refund failed.';
              });
            }
          },
          error: (err) => {
            this.updateViewState(() => {
              this.submitting = false;
              this.error =
                err?.error?.message || err?.message || 'Refund failed.';
            });
          },
        });

      return;
    }
  }

  get items(): FormArray<ItemFG> {
    return this.form.get('items') as FormArray<ItemFG>;
  }

  /** Template helper to build 0..max for <select> options */
  range(max: number): number[] {
    return Array.from({ length: Math.max(0, max) + 1 }, (_, i) => i);
  }
}
